/**
 * WorkflowRuns — ADR-177 D3, this workflow's runs, on this workflow's page.
 *
 * WHY IT MOVED HERE. `/fleet-editor/runs` was a page you navigated away to, and the run
 * you wanted was almost always the one for the workflow you were already looking at. The
 * owner's reading of it, 2026-09-03: *"it is time to move the /runs page to a section on
 * the workflow editor page. Context is then for that specific workflow … I don't think you
 * need to necessarily be in edit mode to see it since there are no actions that can be
 * taken."*
 *
 * Correct, and the gate was worse than that: the whole assistant dock was `editMode &&`,
 * so the state of the run happening RIGHT NOW was also unreachable without entering edit
 * mode on a workflow you had no intention of editing.
 *
 * ONE SECTION, NOT TWO. The current run IS the newest run — showing "the live one" and
 * "the past ones" in separate places would be the same object with two accounts of itself,
 * which is the duplication the single RunTree renderer just removed one level down. So:
 * current run expanded at the top, earlier runs collapsed beneath it, one component.
 *
 * READ-ONLY BY CONSTRUCTION. Everything that WRITES — driving the next phase, answering a
 * checkpoint, discarding the run — stays in AuthoringRail and is rendered only when the
 * operator is editing. This component cannot start work, so it is safe to show to anyone
 * who can read the workflow.
 */
import { useCallback, useEffect, useState } from 'react'

import { api, type AuthoringRunSummary } from '../api/client'
import { RunTree, duration, elapsedS } from './RunTree'
import type { AuthoringRunState, Phase } from './AuthoringRail'
import { proposalIsSettled, useImprovementStatus } from '../pages/useAwaitingProposal'

/** How many earlier runs to list before asking. Same reasoning as the History tab's five:
 *  the run you want is nearly always the last one, and anything older is a search. */
const EARLIER_SHOWN = 10

/** supersededBy — which run, if any, has replaced each run in `runs`.
 *
 * The engine keeps ONE live run per slot, and the slot is keyed
 * (agent, workflow, OPERATOR) — not (agent, workflow). So supersession is
 * per-operator: my newest run replaces MY earlier ones and says nothing about
 * yours. Deriving it from list position instead ("everything below the top row
 * is superseded") would mark a colleague's genuinely-parked run as replaced by
 * mine and suppress its `waiting on you` — turning a real request for a
 * decision into grey text, which is strictly worse than the clutter it set out
 * to fix.
 *
 * Derived, deliberately: there is no `superseded` column and this needs none.
 * The list already arrives newest-first for the slot, so the first run seen for
 * an operator is that operator's live one and every later sighting is history.
 *
 * `runs` MUST be newest-first (the order /activities/authoring-runs returns).
 */
export function supersededBy(runs: AuthoringRunSummary[]): Map<string, AuthoringRunSummary> {
  const newestPerOperator = new Map<string, AuthoringRunSummary>()
  const out = new Map<string, AuthoringRunSummary>()
  for (const run of runs) {
    const newer = newestPerOperator.get(run.operator)
    if (newer) out.set(run.id, newer)
    else newestPerOperator.set(run.operator, run)
  }
  return out
}

export function WorkflowRuns(props: {
  agent: string
  workflow: string
  /** Edit mode. Offers "ask for this phase again" on the CURRENT run only — the engine
   *  keeps one run per slot, so an earlier run has nothing to rewind: it is history, not
   *  state, and a control that appeared to rewind it would rewind the current one. */
  canRedrive?: boolean
  /** How many runs the record holds, reported upward.
   *
   *  THE RAIL'S HEADLINE NEEDS IT AND CANNOT SEE IT. The rail renders this component as a
   *  CHILD, so it has no way to know whether the record is empty — and without that it said
   *  "No authoring run on this workflow yet." over five runs, one of them still going
   *  (reported by the operator with a screenshot, 2026-09-17). Reported rather than fetched
   *  twice: this component already has the list. */
  onHistoryCount?: (n: number) => void
  /** Whether this list is offering "Resume this run", reported upward.
   *
   *  THE RAIL'S HEADLINE SPEAKS ABOUT THIS CONTROL AND CANNOT SEE IT. It said recorded runs
   *  "cannot be resumed" above the card that resumes one — the sentence was written before
   *  the resume route existed. The gate lives here (the newest row is not the live run, it is
   *  unfinished, and the reader is editing), so it is reported rather than re-derived. */
  onResumable?: (resumable: boolean) => void
  /** The id of the run in the LIVE SLOT, or null when the slot is empty.
   *
   *  `current` here is the NEWEST RECORD ENTRY, which is not the same thing — and the
   *  difference decides which controls can work. AuthoringRedrivePhase calls loadRun and
   *  answers 404 for an empty slot, so offering "Ask for this phase again…" on a
   *  record-only run is a link to a 404. Reported by the operator with a screenshot,
   *  2026-09-17, on spartan 20260917T174002Z-1a0d63.
   *
   *  undefined means "not known yet" and is treated as not-live: the safe direction is to
   *  withhold a control that would 404 rather than to offer one that cannot work. */
  liveRunId?: string | null
  /** Signed-in operator. Resume must target only a run owned by this viewer. */
  viewer: string | null
  /** A run deep-linked into the page (`?run=…`), which opens expanded instead of the
   *  newest. This is what keeps a run URL shareable now that /runs/:id lands here. */
  selectedRunId?: string
  onSelectRun?: (id: string) => void
}) {
  const { agent, workflow, selectedRunId, onSelectRun, canRedrive,
    onHistoryCount, onResumable, liveRunId, viewer } = props
  const [runs, setRuns] = useState<AuthoringRunSummary[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'not_configured'>('loading')
  const [err, setErr] = useState('')
  const [openId, setOpenId] = useState<string | null>(selectedRunId ?? null)
  const [showEarlier, setShowEarlier] = useState(false)
  // EARLIER RUNS START COLLAPSED (owner request, 2026-09-16).
  //
  // The current run is what an operator is working on; everything above it on the page was
  // pushed down by a list of finished ones. Collapsing is the MENU, not the state — the
  // count stays visible and one click opens it, which is the same rule the superseded group
  // below already follows. Deliberately not persisted: a durable hide works against the one
  // thing this list is for, which is a second person working out why a phase kept refusing.
  const [showEarlierLive, setShowEarlierLive] = useState(false)
  const [showAllSuperseded, setShowAllSuperseded] = useState(false)

  const load = useCallback(async () => {
    if (!agent || !workflow) return
    try {
      const r = await api.authoringRunList({ agent, workflow, limit: 50 })
      if (r.not_configured) { setState('not_configured'); return }
      setRuns(r.runs || [])
      // REPORTED ON EVERY LOAD, including an empty one: the rail's headline has to be able
      // to say "no runs yet" as well as "N runs that cannot be resumed", and reporting only
      // the non-empty case would leave the first sentence unreachable.
      onHistoryCount?.((r.runs || []).length)
      setState('ready')
    } catch (e) {
      // NOT an empty list. "Could not read" and "has never authored anything" send a
      // reader to completely different places.
      setErr(e instanceof Error ? e.message : 'the run history could not be read')
      setState('error')
    }
  }, [agent, workflow, onHistoryCount])

  useEffect(() => { void load() }, [load])
  // Polled, because the newest run is usually one that is still going. Paused while the
  // tab is hidden: a background tab polling a fleet forever is a cost with no reader.
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) void load() }, 5000)
    return () => clearInterval(t)
  }, [load])

  useEffect(() => { if (selectedRunId) setOpenId(selectedRunId) }, [selectedRunId])

  // A DEEP LINK OUTLIVES THE RUN IT POINTS AT, AND SAID NOTHING.
  //
  // REPORTED FROM PRODUCTION, 2026-09-20. An operator started a run on orion and the
  // address bar still read `?run=20260919T180650Z-355598` — a run from the previous day.
  // They read that page as their new run and reported "analysis is not finishing"; it was
  // seventeen hours dead and nothing on screen said which run they were looking at.
  //
  // `onSelectRun` writes `?run=<id>` on EVERY card click (Workflows.tsx), with
  // `replace: true`, and nothing ever clears it. So one click yesterday pins the URL —
  // and the bookmark, and the reload — to a historical run for as long as that address
  // survives, while new runs start underneath it.
  //
  // ANNOUNCED, NOT SILENTLY REWRITTEN. ADR-177 made the run URL shareable on purpose, and
  // a link that redirects itself is not shareable. The repo's own rule for this shape is
  // one component over: "AND THE LINK IS NOT LOST, it is announced" — collapse the menu,
  // never the state. So the link keeps working and the page says what it is showing.
  const linkedRunIsStale = !!selectedRunId && !!liveRunId && selectedRunId !== liveRunId

  // THE RESUME GATE, COMPUTED ONCE AND USED TWICE: it decides the card's control below and
  // is reported upward so the rail's headline describes the same page the reader is on.
  //
  // Held above the early returns because it is a hook. The API returns newest first; choose
  // this viewer's newest row first, then offer Resume only if that row is unfinished.
  const viewerEmail = viewer?.trim().toLowerCase()
  const own = viewerEmail
    ? runs.find(run => run.operator.trim().toLowerCase() === viewerEmail)
    : undefined
  const resumable = state === 'ready' && canRedrive && liveRunId !== undefined &&
    own && own.id !== liveRunId && !isFinished(own) ? own : undefined
  const resumableRunId = resumable?.id ?? null
  useEffect(() => { onResumable?.(resumableRunId !== null) }, [resumableRunId, onResumable])

  const resume = async (id: string) => {
    await api.resumeAuthoringRun(agent, workflow, id)
    await load()
  }

  if (state === 'loading') return <div className="p-2 text-[11px] text-gray-500">Reading this workflow&apos;s runs…</div>

  if (state === 'not_configured') {
    return (
      <div className="m-2 rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900">
        This Mission Control has no session store, so authoring runs are not recorded.
        Runs still happen — nothing is keeping them.
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="m-2 rounded border border-red-300 bg-red-50 p-2 text-[11px] text-red-800">
        <strong>Could not read this workflow&apos;s runs.</strong> {err}
        <button type="button" onClick={() => void load()}
          className="ml-2 rounded border border-red-300 bg-white px-1.5 py-0.5 hover:bg-red-100">
          Retry
        </button>
      </div>
    )
  }

  if (runs.length === 0) {
    return (
      <div className="m-2 rounded border border-gray-200 bg-gray-50 p-2 text-[11px] text-gray-600">
        No authoring runs on this workflow yet. Ask the assistant for a change and one starts.
      </div>
    )
  }

  const [current, ...earlier] = runs
  // Callers that have not loaded the slot identity yet retain the historical newest-row
  // presentation. Once the identity is known, only an exact match is live; null explicitly
  // means the slot is empty.
  const currentIsLive = liveRunId === undefined || current.id === liveRunId
  // Supersession is per-operator (see supersededBy). An earlier run that is still the
  // newest for ITS operator is not history — it may be parked waiting on that person —
  // so it keeps a normal row under the SAME "Earlier runs" heading it always had. Only
  // genuinely-replaced runs are demoted into the collapsed group below. Renaming the
  // existing heading was not part of the ask and would have churned a contract test
  // pinning it, so it stays.
  const replaced = supersededBy(runs)
  const earlierLive = earlier.filter(r => !replaced.has(r.id))
  const earlierSuperseded = earlier.filter(r => replaced.has(r.id))
  // A deep link must still win over the collapse, or a shared ?run=… URL lands on a
  // section the reader then has to know to expand.
  const deepLinkIsSuperseded = !!selectedRunId && replaced.has(selectedRunId)
  // EARLIER RUNS STAY COLLAPSED UNTIL THE READER OPENS THEM. No deep-link override.
  //
  // THE FIRST VERSION HAD ONE AND IT DEFEATED THE FEATURE ENTIRELY. It read
  // `selectedRunId` — "a shared ?run=… URL must not land on a section the reader has to
  // know to expand" — and that is reasonable about a LINK and wrong about this prop.
  // `onSelectRun` is the page's `setDeepLinkedRunId`, which writes `?run=<id>` into the
  // URL on every click, and `selectedRunId` is read straight back out of that param. So
  // clicking any run left the URL carrying it, the override then evaluated true, and the
  // section was expanded permanently — for the rest of the session and on every reload of
  // that address. Reported against the live build with a screenshot: `▾ EARLIER RUNS (3)`
  // open on load with all three cards showing, which is what "collapse" was asked to stop.
  //
  // AND THE LINK IS NOT LOST, it is announced. A reader who follows a shared link to an
  // earlier run needs to know the run is in there; the toggle says so, and one click shows
  // it. That keeps the default the owner asked for and still makes the link discoverable —
  // collapsing the menu, never the state.
  const linkedRunIsInEarlier = !!selectedRunId && earlierLive.some(r => r.id === selectedRunId)
  const earlierLiveOpen = showEarlierLive
  const supersededOpen = showEarlier || deepLinkIsSuperseded
  const shownSuperseded = supersededOpen
    ? (showAllSuperseded ? earlierSuperseded : earlierSuperseded.slice(0, EARLIER_SHOWN))
    : []

  return (
    <div className="space-y-2 p-2">
      {linkedRunIsStale && (
        <div className="rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900">
          <strong>This link points at an earlier run.</strong> The address carries{' '}
          <code className="font-mono">{selectedRunId}</code>, and the run in the slot right
          now is <code className="font-mono">{liveRunId}</code> — so what you are reading is
          history, not the live run.
          <button type="button" onClick={() => onSelectRun?.(liveRunId!)}
            className="ml-2 rounded border border-amber-400 bg-white px-1.5 py-0.5 font-medium hover:bg-amber-100">
            Show the live run
          </button>
        </div>
      )}
      <RunCard
        run={current} open={currentIsLive || openId === current.id}
        onToggle={() => { setOpenId(openId === current.id ? '' : current.id); onSelectRun?.(current.id) }}
        latest={currentIsLive}
        // REDRIVE ONLY ON THE RUN THAT IS ACTUALLY IN THE SLOT.
        //
        // The rule was already written at this component's mount site — "an earlier run has
        // nothing to rewind — it is history, not state" — and was not enforced: `current` is
        // the newest RECORD entry, so with an empty slot the newest row inherited a control
        // that answers 404.
        onRedrive={canRedrive && currentIsLive ? async (phase, note) => {
          await api.redriveAuthoringPhase(agent, workflow, String(phase), note)
          await load()
        } : undefined}
        // AND WHEN IT IS NOT THE LIVE RUN, THE OTHER CONTROL. The record holds the whole
        // document, so the alternative to resuming is paying for six phases again.
        onResume={resumableRunId === current.id ? () => resume(current.id) : undefined}
      />
      {earlierLive.length > 0 && (
        <div className="border-t border-gray-200 pt-1.5">
          {/* THE HEADING IS THE CONTROL. It kept its text and its count so the contract
              test pinning "Earlier runs (N)" still describes what a reader sees; what
              changed is that it collapses. */}
          <button type="button" onClick={() => setShowEarlierLive(!earlierLiveOpen)}
            aria-expanded={earlierLiveOpen}
            className="flex w-full items-center gap-1 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600">
            <span aria-hidden="true">{earlierLiveOpen ? '▾' : '▸'}</span>
            Earlier runs ({earlierLive.length})
            {!earlierLiveOpen && linkedRunIsInEarlier && (
              <span className="normal-case tracking-normal text-blue-600"> — includes the linked run</span>
            )}
          </button>
          {earlierLiveOpen && earlierLive.map(r => (
            <RunCard key={r.id} run={r} open={openId === r.id}
              onToggle={() => { setOpenId(openId === r.id ? '' : r.id); onSelectRun?.(r.id) }}
              onResume={resumableRunId === r.id ? () => resume(r.id) : undefined} />
          ))}
        </div>
      )}

      {/* SUPERSEDED RUNS COLLAPSE TO ONE LINE.
          Not hidden and not archived: the history exists so a second person can work out
          why a stage kept refusing, and durable hiding works against the only thing this
          list is for. Collapsed is the menu; the state stays readable one click away. */}
      {earlierSuperseded.length > 0 && (
        <div className="border-t border-gray-200 pt-1.5">
          <button type="button" onClick={() => setShowEarlier(!supersededOpen)}
            className="text-[10px] text-gray-500 hover:text-gray-800 hover:underline">
            {supersededOpen ? '▾' : '▸'} {earlierSuperseded.length} superseded{' '}
            {earlierSuperseded.length === 1 ? 'run' : 'runs'}
            {supersededOpen ? '' : ' — show'}
          </button>
          {shownSuperseded.map(r => (
            <RunCard key={r.id} run={r} open={openId === r.id} superseded={replaced.get(r.id)}
              onSelectSuperseding={() => {
                const n = replaced.get(r.id)
                if (n) { setOpenId(n.id); onSelectRun?.(n.id) }
              }}
              onToggle={() => { setOpenId(openId === r.id ? '' : r.id); onSelectRun?.(r.id) }}
              onResume={resumableRunId === r.id ? () => resume(r.id) : undefined} />
          ))}
          {supersededOpen && !showAllSuperseded && earlierSuperseded.length > EARLIER_SHOWN && (
            <button type="button" onClick={() => setShowAllSuperseded(true)}
              className="mt-1 text-[10px] text-indigo-700 hover:underline">
              Show {earlierSuperseded.length - EARLIER_SHOWN} older
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** One run: a summary line always, its tree when open.
 *
 * The tree is fetched ON OPEN rather than with the list — a workflow with fifty runs would
 * otherwise pull fifty documents to draw ten summary lines. */
function RunCard({ run, open, onToggle, latest, superseded, onSelectSuperseding, onRedrive,
  onResume }: {
  run: AuthoringRunSummary
  open: boolean
  onToggle: () => void
  latest?: boolean
  /** The run that REPLACED this one, when one has (see supersededBy). Set means this row
   *  is history: it is demoted, and — the point of the whole change — it stops claiming
   *  `waiting on you`, because the decision it is parked on has been overtaken. */
  superseded?: AuthoringRunSummary
  /** Jump to the run that replaced this one. A dead end with no route to the live run is
   *  the same trap the "way back" link at the top of AuthoringRuns fixed. */
  onSelectSuperseding?: () => void
  onRedrive?: (phase: Phase, note: string) => Promise<void>
  /** Put this RECORDED run back in the live slot (ADR-166).
   *
   *  Offered only when the row is NOT the live run and is not finished, which is exactly
   *  when redrive cannot work — the two are alternatives, never both. */
  onResume?: () => Promise<void>
}) {
  // WHAT THE PROPOSAL IS NOW — see useImprovementStatus. The row carries improvement_id
  // already; nothing had ever asked what became of it.
  const proposalStatus = useImprovementStatus(run.improvement_id)
  const [doc, setDoc] = useState<AuthoringRunState | null>(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)
  const live = !run.outcome || run.outcome === 'in-progress'

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const r = await api.authoringRunDetail(run.id)
      setDoc(r.run?.run ?? null)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'the run could not be read')
    } finally {
      setLoading(false)
    }
  }, [run.id])

  useEffect(() => { if (open) void load() }, [open, load])
  // A live run's tree is re-read as it advances; a finished one is fetched once. Polling a
  // run that ended would be a request per five seconds that can never return anything new.
  useEffect(() => {
    if (!open || !live) return
    const t = setInterval(() => { if (!document.hidden) void load() }, 5000)
    return () => clearInterval(t)
  }, [open, live, load])

  const total = elapsedS(run.opened_at, run.closed_at)
  return (
    <div className={`rounded border ${latest ? 'border-gray-300' : 'border-gray-200'} ${superseded ? 'bg-gray-50 opacity-70' : 'bg-white'}`}>
      <button type="button" onClick={onToggle}
        className="flex w-full flex-wrap items-baseline gap-2 px-2 py-1 text-left hover:bg-gray-50">
        <span className="text-[10px] text-gray-400">{open ? '▾' : '▸'}</span>
        {/* "CURRENT" MEANS STILL IN PLAY, NOT "NEWEST".
            REPORTED BY THE FLEET OWNER 2026-09-17 after approving and deploying PR #1184:
            "I alreay deployed that to production. it now should be included in Earlier runs
            or superceded. it is confusing since it is not current." A run whose proposal has
            merged, deployed or been declined is finished with — the badge says so instead. */}
        {latest && (
          proposalIsSettled(proposalStatus)
            ? <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {proposalStatus}
              </span>
            : <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Current</span>
        )}
        <Outcome run={run} />
        <span className="text-[11px] text-gray-800">{run.task || 'Untitled run'}</span>
        <span className="ml-auto text-[10px] text-gray-500">
          {run.refusals > 0 && <span className="text-red-700">{run.refusals} refused · </span>}
          {/* "NOT FINISHED" AND "WAITING ON YOU" ARE DIFFERENT STATES.
              Measured on spartan run 20260908T203353Z-3b7607: the line read "18 refused ·
              still going" while the run was `awaiting-decision`, auto-advance was paused,
              and its last verdict was "waiting on a checkpoint decision at implementation:
              1 of 1 open item(s) routed for review". ADR-166 D6 had routed an item to a
              person, and the person it was waiting for was reading a line saying the machine
              was still working. The outcome was on the row the whole time; this printed
              "still going" whenever closed_at was absent and never looked at it.
              The rail's pacing banner already says "Waiting on you at implementation" — two
              surfaces disagreeing about one run is its own defect, and the list is the one
              an operator scans first. */}
          {run.closed_at
            ? duration(total)
            : run.outcome === 'awaiting-decision'
              // SUPERSEDED BEATS awaiting-decision. The amber is a request for a decision,
              // and once a newer run exists on this operator's slot that request has been
              // overtaken: answering it cannot advance anything. Measured on matthaios —
              // run …153955Z-001a78 read "8 refused · waiting on you" while …155431Z-eef39a
              // had already superseded it and opened PR #470. This is the same defect the
              // comment above fixed one layer in (a surface disagreeing with the run's own
              // state), pointed at a stale row instead of a live one.
              ? superseded
                ? <span className="text-gray-500">parked at {run.phase || 'a phase'}, superseded</span>
                : <span className="font-medium text-amber-700">waiting on you</span>
              : FINISHED_OUTCOMES.has(run.outcome)
                ? run.outcome
                : 'still going'} · {new Date(run.opened_at).toLocaleString()}
        </span>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-2 py-1">
          {/* FIRST THING IN AN EXPANDED SUPERSEDED RUN. A ?run=… deep link opens this card
              directly, so without a banner at the top the reader gets a parked run, eight
              refusals and a checkpoint with nothing saying it has already been replaced —
              which is exactly the link that prompted this change. */}
          {superseded && (
            <div className="mb-1 rounded border border-gray-300 bg-white px-1.5 py-1 text-[10px] text-gray-700">
              ⤳ Superseded by{' '}
              <button type="button" onClick={onSelectSuperseding}
                className="font-mono text-indigo-700 hover:underline">
                {superseded.id}
              </button>
              {superseded.outcome && <> ({superseded.outcome})</>}. Answering this run&apos;s
              checkpoint cannot advance anything — the slot has moved on.
            </div>
          )}
          {/* THE RUN IS IN THE RECORD AND NOT IN THE SLOT — say so, and offer the way back.
              Reported by the operator with a screenshot, 2026-09-17: "there is no place to
              select continue" over a run that had reached `results`. The phase controls
              render off the LIVE run, so with an empty slot they are all absent and the
              page said nothing about why. */}
          {onResume && (
            <div className="mb-1 rounded border border-sky-300 bg-sky-50 px-1.5 py-1 text-[10px] text-sky-900">
              This run&apos;s <strong>live state is gone</strong> — its record is complete, but
              nothing is in the editor&apos;s slot, so the phase controls cannot act on it.
              <button type="button" onClick={() => { void onResume() }}
                className="ml-1 font-medium text-sky-800 underline hover:text-sky-950">
                Resume this run
              </button>{' '}
              puts it back and re-arms the countdown — the alternative is starting over.
            </div>
          )}
          <div className="mb-1 text-[10px] text-gray-500">
            {run.operator}
            {/* ADR-183 — the vendor, then the model. A model name alone stopped
                identifying a run the moment five vendors could offer one, so the
                provider leads when it is known. */}
            {run.provider && <> · {run.provider}</>}
            {run.model && <> · {run.model}</>}
            {/* THE ANSWERED MODEL, SHOWN ONLY WHEN IT DISAGREES.
                Always would double the row's width to restate the request; never
                would hide a vendor resolving an alias to a different snapshot, or
                substituting under load — the thing an evaluation most needs to see.
                So it appears exactly when it is news. */}
            {run.response_model && run.response_model !== run.model && (
              <> · answered <span className="font-mono">{run.response_model}</span></>
            )}
            {/* An unattributed run SAYS SO rather than looking like a default one.
                "never measured" and "ran on the default" are different facts, and
                ADR-183 exists so they stop being indistinguishable. */}
            {/* ADR-194 — WHICH LAYER chose it. A run on gpt-6-astra because the
                FLEET pinned it is different evidence from the same model someone
                picked by hand, and a list that shows only the model cannot tell
                a reader which they are looking at. */}
            {run.model_source === 'fleet' && <> · <span title="pinned by this fleet">fleet pin</span></>}
            {run.model_source === 'operator' && <> · <span title="pinned by the author">their pin</span></>}
            {run.model_source === 'request' && <> · <span title="chosen for this turn only">one-off</span></>}
            {/* A substituted run is NOT evidence about the pinned model: the pin
                could not be called and the deployment default answered. Marked
                on the row so it can be excluded while scanning, rather than
                found later by opening it. */}
            {run.model_substituted && (
              <> · <span className="text-amber-700" title="the pinned model could not be called; the deployment default answered">substituted</span></>
            )}
            {!run.provider && !run.model && (
              <> · <span className="text-gray-400">model unknown</span></>
            )}
            {/* The build that produced it (ADR-175) — the fact that makes two runs
                comparable, and the one that used to cost a production run to establish. */}
            {run.mc_version && <> · build <span className="font-mono">{run.mc_version.slice(0, 8)}</span></>}
          </div>
          {loading && !doc && <div className="text-[10px] text-gray-500">reading…</div>}
          {err && (
            <div className="rounded border border-red-300 bg-red-50 p-1.5 text-[10px] text-red-800">
              {err}
              <button type="button" onClick={() => void load()}
                className="ml-2 rounded border border-red-300 bg-white px-1.5 hover:bg-red-100">Retry</button>
            </div>
          )}
          {doc === null && !loading && !err && (
            // A row with no document is a degraded record, not an empty run — saying
            // "no phases" here would report the storage problem as a fact about the work.
            <div className="text-[10px] text-amber-800">
              This run&apos;s detail was not stored, so its phases cannot be shown.
            </div>
          )}
          {doc && <RunTree run={doc} onRedrive={onRedrive} />}
        </div>
      )}
    </div>
  )
}

// FINISHED_OUTCOMES are the states a run does not come back from.
//
// `closed_at` is NOT set for a run that reaches `done` and proposes — measured on spartan,
// the list returned outcome=proposed and outcome=propose-failed both with closed_at absent —
// so a line keyed only on closed_at printed "still going" over finished work, including the
// successful runs. The first version of this fix special-cased `awaiting-decision` alone,
// which repaired the case in front of me and left its siblings lying.
//
// `refused` is deliberately absent: a refused phase is RETRIED, so a refused run may still be
// going, and calling it finished would be the opposite lie.
const FINISHED_OUTCOMES = new Set(['proposed', 'propose-failed', 'no-change', 'complete'])

// isFinished is "this run does not come back", for deciding whether RESUME is worth offering.
//
// PHASE 'done' COUNTS AS WELL AS THE OUTCOMES. A run can sit at `done` before its outcome is
// written — the propose is a separate step — and putting a finished run back in the slot
// would make it the thing every later turn merges into. The backend refuses it too
// (authoring_resume_from_record.go); this keeps the button off a control that would 400.
function isFinished(run: AuthoringRunSummary): boolean {
  return FINISHED_OUTCOMES.has(run.outcome) || run.phase === 'done' || run.outcome === 'cancelled'
}

function Outcome({ run }: { run: AuthoringRunSummary }) {
  // An unfinished run reports its PHASE, never elapsed time (ADR-167 D2).
  if (!run.outcome || run.outcome === 'in-progress') {
    return (
      <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] text-indigo-800">
        {run.phase ? `at ${String(run.phase).replace(/[-_]/g, ' ')}` : 'in flight'}
      </span>
    )
  }
  const cls =
    run.outcome === 'complete' ? 'bg-emerald-100 text-emerald-800'
      : run.outcome === 'refused' ? 'bg-red-100 text-red-800'
        : run.outcome === 'awaiting-decision' ? 'bg-amber-100 text-amber-900'
          : 'bg-gray-100 text-gray-700'
  return <span className={`rounded px-1.5 py-0.5 text-[10px] ${cls}`}>{run.outcome}</span>
}
